import React, { useContext, useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { WidgetContext } from "../../store/widgetContext";
import { axiosKnowledge } from "../../configs/http-config";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";

// Links inside an article open outside the widget; the sandbox (no scripts, no same-origin) keeps the
// article's HTML from running anything in the widget's origin.
const asFrameDocument = (html) => `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"></head><body>${html}</body></html>`;

/**
 * One public article, read through the knowledge service — `GET /documents/public/:id/content`, the
 * same `{tenantId, uuid, type:'public'}` scope as the article list. Bug 2026-10-03: this used to
 * iframe the record's `url`, a private object-store address that answers AccessDenied.
 */
export function useArticleBody(documentId, { uuid, tenantId, botId }) {
	const { hostName, pathName } = useContext(WidgetContext);
	const [state, setState] = useState({ loading: false, html: null, error: false });
	useEffect(() => {
		if (!documentId) { setState({ loading: false, html: null, error: false }); return undefined; }
		let cancelled = false;
		setState({ loading: true, html: null, error: false });
		axiosKnowledge.get(`/documents/public/${documentId}/content`, {
			params: { uuid, tenantId, botId }, headers: { "X-Host": hostName, "X-Pathname": pathName },
		}).then((response) => {
			const html = response?.data?.data?.html;
			if (cancelled) return;
			if (typeof html !== "string") setState({ loading: false, html: null, error: true });
			else setState({ loading: false, html, error: false });
		}).catch((error) => {
			if (cancelled) return;
			console.error("Error fetching article body:", error);
			setState({ loading: false, html: null, error: true });
		});
		return () => { cancelled = true; };
	}, [documentId, uuid, tenantId, botId, hostName, pathName]);
	return state;
}

export default function Document({ selectedDocument, uuid, tenantId, botId }) {
	const { msg } = useWidgetMsg();
	const body = useArticleBody(selectedDocument?._id, { uuid, tenantId, botId });

	if (!selectedDocument) {
		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					background: "white",
					borderRadius: "16px",
					overflow: "hidden",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						flex: 1,
						textAlign: "center",
					}}
				>
					<Typography variant="body2" sx={{ fontSize: "14px", color: "#808080" }}>
						No document selected
					</Typography>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				background: "white",
				borderRadius: "16px",
				overflow: "hidden",
			}}
		>
			<Box sx={{ flex: 1, overflow: "hidden", background: "white", position: "relative", padding: "10px", display: "flex", flexDirection: "column" }}>
				{body.loading ? (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
						<CircularProgress size={24} />
					</Box>
				) : !selectedDocument._id && selectedDocument.url ? (
					// A chat CITATION (`{name, url}`, no article id): the cited page's own address, as before.
					// An article citation whose url is the object store still cannot load here — the
					// citation needs the article id (reported follow-up, bug 2026-10-03).
					<iframe
						src={selectedDocument.url}
						width="100%"
						height="100%"
						frameBorder="0"
						title={selectedDocument.title || "Document Content"}
						style={{ border: "none", display: "block", flexGrow: 1, minWidth: 0 }}
					/>
				) : body.html !== null ? (
					<iframe
						srcDoc={asFrameDocument(body.html)}
						sandbox="allow-popups allow-popups-to-escape-sandbox"
						width="100%"
						height="100%"
						frameBorder="0"
						title={selectedDocument.title || "Document Content"}
						style={{ border: "none", display: "block", flexGrow: 1, minWidth: 0 }}
					/>
				) : (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							textAlign: "center",
						}}
					>
						<Typography variant="body2" sx={{ fontSize: "14px", color: "#808080" }}>
							{msg("help.article_unavailable", "This article could not be loaded")}
						</Typography>
					</Box>
				)}
			</Box>
		</Box>
	);
}

